/**
 * Product page behaviors:
 * - Variant selection via option pills: resolves the matching variant,
 *   updates price, compare-at, stock chip, ATC state, selected-value labels,
 *   main image and the URL (?variant=).
 * - Quantity stepper.
 * - Thumbnail gallery switching.
 * Add-to-cart submit itself is handled by the wrapped <ws-quick-add>.
 */
window.WsProductForm = class WsProductForm extends HTMLElement {
  #abort = null;
  #product = null;
  #stickyObserver = null;
  #noticeTimer = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    try {
      this.#product = JSON.parse(this.querySelector('[data-product-json]')?.textContent ?? 'null');
    } catch {
      this.#product = null;
    }

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-input]')) this.#onOptionChange();
      if (event.target.matches('.ws-product__qty-input')) this.#syncQty(event.target);
    }, { signal });

    this.addEventListener('click', (event) => {
      const qty = event.target.closest('[data-qty-change]');
      if (qty) {
        const input = qty.closest('.ws-product__qty')?.querySelector('.ws-product__qty-input');
        if (input) {
          input.value = Math.max(1, Number(input.value || 1) + Number(qty.dataset.qtyChange));
          this.#syncQty(input);
        }
        return;
      }

      const thumb = event.target.closest('[data-thumb]');
      if (thumb) {
        this.#setMainImage(thumb.dataset.imageUrl, thumb);
        return;
      }

      if (event.target.closest('[data-sticky-add]')) {
        this.#onStickyAdd();
        return;
      }

      if (event.target.closest('[data-lightbox-open]')) {
        this.#openLightbox();
        return;
      }
      if (event.target.closest('[data-lightbox-close]') || event.target.matches('[data-lightbox]')) {
        this.querySelector('[data-lightbox]')?.close();
        return;
      }
      if (event.target.closest('[data-lightbox-prev]')) {
        this.#lightboxStep(-1);
        return;
      }
      if (event.target.closest('[data-lightbox-next]')) {
        this.#lightboxStep(1);
        return;
      }
      const lightboxThumb = event.target.closest('[data-lightbox-to]');
      if (lightboxThumb) {
        this.#lightboxGo(Number(lightboxThumb.dataset.lightboxTo));
        return;
      }

      const anchor = event.target.closest('.ws-product__anchor');
      if (anchor) this.#scrollToAnchor(event, anchor);
    }, { signal });

    this.#initStickyBar();
    this.#initZoom(signal);
    this.#initLightbox(signal);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#stickyObserver?.disconnect();
    clearTimeout(this.#noticeTimer);
  }

  #initStickyBar() {
    const bar = this.querySelector('[data-sticky-atc]');
    const buyRow = this.querySelector('.ws-product__buy-row');
    if (!bar || !buyRow || !('IntersectionObserver' in window)) return;

    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('ws-product__sticky--visible', scrolledPast);
      bar.setAttribute('aria-hidden', String(!scrolledPast));
      // Keep the off-screen bar out of the tab order / AT tree while hidden.
      bar.inert = !scrolledPast;
    }, { threshold: 0 });
    this.#stickyObserver.observe(buyRow);
  }

  #initZoom(signal) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const photo = this.querySelector('.ws-product__photo');
    if (!photo) return;

    photo.addEventListener('pointerenter', () => photo.classList.add('ws-product__photo--zooming'), { signal });
    photo.addEventListener('pointerleave', () => photo.classList.remove('ws-product__photo--zooming'), { signal });
    photo.addEventListener('pointermove', (event) => {
      const rect = photo.getBoundingClientRect();
      const image = photo.querySelector('.ws-product__photo-image');
      if (!image) return;
      image.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
      image.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
    }, { signal });
  }

  #initLightbox(signal) {
    const lightbox = this.querySelector('[data-lightbox]');
    if (!lightbox) return;

    lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.#lightboxStep(-1);
      if (event.key === 'ArrowRight') this.#lightboxStep(1);
    }, { signal });

    this.querySelector('[data-lightbox-track]')?.addEventListener('scroll', () => {
      this.#markActiveLightboxThumb();
    }, { signal, passive: true });
  }

  get #lightboxIndex() {
    const track = this.querySelector('[data-lightbox-track]');
    if (!track) return 0;
    return Math.round(track.scrollLeft / track.clientWidth);
  }

  #openLightbox() {
    const lightbox = this.querySelector('[data-lightbox]');
    if (!lightbox) return;
    lightbox.showModal();

    // Start on the image currently shown in the gallery (active thumb)
    const thumbs = [...this.querySelectorAll('[data-thumb]')];
    const activeIndex = Math.max(0, thumbs.findIndex((t) => t.classList.contains('ws-product__thumb--active')));
    this.#lightboxGo(activeIndex, 'instant');
  }

  #lightboxGo(index, behavior = 'smooth') {
    const track = this.querySelector('[data-lightbox-track]');
    if (!track) return;
    const slide = track.children[index];
    if (slide) track.scrollTo({ left: slide.offsetLeft, behavior });
    this.#markActiveLightboxThumb(index);
  }

  #lightboxStep(direction) {
    const track = this.querySelector('[data-lightbox-track]');
    if (!track) return;
    const count = track.children.length;
    const next = (this.#lightboxIndex + direction + count) % count;
    this.#lightboxGo(next);
  }

  #markActiveLightboxThumb(index = this.#lightboxIndex) {
    const thumbs = this.querySelectorAll('[data-lightbox-to]');
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('ws-product__lightbox-thumb--active', i === index);
    });
  }

  #syncQty(changed) {
    const value = Math.max(1, Number(changed.value || 1));
    changed.value = value;
    for (const input of this.querySelectorAll('.ws-product__qty-input')) {
      if (input !== changed) input.value = value;
    }
  }

  #onStickyAdd() {
    const form = this.querySelector('.ws-product__form');

    // Always scroll back up to the buy area first — with or without options,
    // and also while the add request runs — so the user sees what happens.
    const scrollTarget = this.querySelector('.ws-product__selectors') ?? this.querySelector('.ws-product__buy-row');
    scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // No options (single variant) or no product data to validate against:
    // just try the regular add-to-cart.
    if (!this.hasAttribute('data-has-options') || !this.#product) {
      form?.requestSubmit();
      return;
    }

    // Options exist: purchasable combo → add to cart; otherwise show the
    // "select your options" prompt.
    const selected = this.#selectedOptions;
    const variant = this.#product.variants.find((v) =>
      v.options.every((value, index) => value === selected[index])
    );

    if (variant?.available) {
      form?.requestSubmit();
      return;
    }

    const notice = this.querySelector('[data-option-notice]');
    if (notice) {
      notice.hidden = false;
      notice.setAttribute('role', 'alert');
      clearTimeout(this.#noticeTimer);
      this.#noticeTimer = setTimeout(() => {
        notice.hidden = true;
      }, 5000);
    }
  }

  get #selectedOptions() {
    const options = [];
    for (const fieldset of this.querySelectorAll('[data-option-index]')) {
      options[Number(fieldset.dataset.optionIndex)] =
        fieldset.querySelector('[data-option-input]:checked')?.value ?? null;
    }
    return options;
  }

  #onOptionChange() {
    if (!this.#product) return;

    const selected = this.#selectedOptions;
    const variant = this.#product.variants.find((v) =>
      v.options.every((value, index) => value === selected[index])
    );

    for (const fieldset of this.querySelectorAll('[data-option-index]')) {
      const label = fieldset.querySelector('[data-selected-value]');
      if (label) label.textContent = selected[Number(fieldset.dataset.optionIndex)] ?? '';
    }

    if (!variant) {
      this.#setAvailability(false);
      return;
    }

    this.querySelector('[data-variant-id]')?.setAttribute('value', variant.id);

    const price = this.querySelector('[data-price]');
    if (price) price.textContent = this.#formatMoney(variant.price);

    const compare = this.querySelector('[data-compare-price]');
    if (compare) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      compare.hidden = !onSale;
      compare.textContent = onSale ? this.#formatMoney(variant.compare_at_price) : '';
    }

    this.#setAvailability(variant.available);

    const skuWrapper = this.querySelector('[data-sku-wrapper]');
    const sku = this.querySelector('[data-sku]');
    if (skuWrapper && sku) {
      skuWrapper.hidden = !variant.sku;
      sku.textContent = variant.sku ?? '';
    }

    if (variant.featured_image?.src) this.#setMainImage(variant.featured_image.src);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
  }

  #setAvailability(available) {
    const stock = this.querySelector('[data-stock]');
    const stockText = this.querySelector('[data-stock-text]');
    const button = this.querySelector('[name="add"]');
    const label = button?.querySelector('[data-add-label]');

    stock?.classList.toggle('ws-product__stock--in', available);
    stock?.classList.toggle('ws-product__stock--out', !available);
    if (stock && stockText) {
      stockText.textContent = available ? stock.dataset.textIn : stock.dataset.textOut;
    }

    if (button) {
      button.toggleAttribute('disabled', !available);
      if (label) {
        label.textContent = available ? button.dataset.textAdd : button.dataset.textSoldOut;
      }
    }

    // Keep the sticky button clickable on option products even when the
    // current combo is unavailable — clicking it guides the user to the
    // selectors instead of dead-ending on a disabled button.
    if (!this.hasAttribute('data-has-options')) {
      this.querySelector('[data-sticky-add]')?.toggleAttribute('disabled', !available);
    }
  }

  #setMainImage(url, thumb = null) {
    const main = this.querySelector('.ws-product__photo img');
    if (!main || !url) return;
    main.srcset = '';
    main.sizes = '';
    main.src = url;

    const sticky = this.querySelector('.ws-product__sticky-image');
    if (sticky) {
      sticky.srcset = '';
      sticky.src = url;
    }

    const active = thumb ?? [...this.querySelectorAll('[data-thumb]')].find((t) => t.dataset.imageUrl === url);
    for (const t of this.querySelectorAll('[data-thumb]')) {
      t.classList.toggle('ws-product__thumb--active', t === active);
    }
  }

  /**
   * Anchor links prefer a matching heading INSIDE the product description
   * (e.g. the "Ingredients" subheading of the rich text); they fall back to
   * the element with the hash id (e.g. the info accordion) when none exists.
   */
  #scrollToAnchor(event, anchor) {
    const keyword = anchor.getAttribute('href').replace('#', '').toLowerCase();
    if (!keyword || keyword === 'description' || keyword === 'reviews') return;

    const headings = document.querySelectorAll(
      '.ws-product-description__content :is(h1, h2, h3, h4, h5, h6, strong)'
    );
    const match = [...headings].find((h) => h.textContent.toLowerCase().includes(keyword));
    if (!match) return;

    event.preventDefault();
    match.style.scrollMarginTop = '120px';
    match.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState({}, '', `#${keyword}`);
  }

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active ?? 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency
    }).format(cents / 100);
  }
}

if (!customElements.get('ws-product-form')) {
  customElements.define('ws-product-form', WsProductForm);
}

/*
 * The buy-box "Write a review" link opens BayEngage's write-review modal
 * directly (by clicking its `._tbr-write-btn`) instead of just scrolling to the
 * reviews section. Falls back to the default #reviews anchor when the BayEngage
 * widget hasn't rendered yet.
 */
if (!window.__wsWriteReviewBound) {
  window.__wsWriteReviewBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.ws-product__write-review');
    if (!link) return;
    const writeBtn = document.querySelector('._tbr-write-btn');
    if (writeBtn) {
      event.preventDefault();
      writeBtn.click();
    }
  });
}
